// LayoutRichTextBlockModal.jsx

import React, { useEffect, useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Utilities
import {
	createEmptyRichTextDocument,
	normalizeRichTextValue,
	serializeRichTextDocument,
} from 'utilities/RichText';

// CSS
import './LayoutRichTextBlockModal.css';

// Components
import Button from 'components/button/Button';
import Modal from 'components/modal/Modal';
import RichTextEditor from 'components/richtext/RichTextEditor';

const EMPTY_RICH_TEXT_SERIALIZED = serializeRichTextDocument(createEmptyRichTextDocument());

/**
 * LayoutRichTextBlockModal
 *
 * @returns {ReactNode} The rendered component.
 * @returns {ReactNode} The rendered component.
 * @returns {ReactNode} The rendered component.
 */
const LayoutRichTextBlockModal = React.memo(({
	documentLabel = 'layout',
	initialValue,
	isOpen,
	onClose,
	onSave,
}) => {

	// ----- CONTEXT ----- //

	const { theme } = useTheme();
	// ----- DEVICE DETECTION ----- //

	const isMobile = useIsMobile();

	// ----- STATE ----- //

	const [draftValue, setDraftValue] = useState(() => normalizeRichTextValue(initialValue));
	const [isSaving, setIsSaving] = useState(false);

	// ----- MEMOS ----- //

	const normalizedInitialValue = useMemo(() => normalizeRichTextValue(initialValue), [initialValue]);
	const normalizedDocumentLabel = useMemo(() => {
		return String(documentLabel || 'layout').trim().toLowerCase() || 'layout';
	}, [documentLabel]);
	const isNewBlock = useMemo(() => {
		return serializeRichTextDocument(normalizedInitialValue.content) === EMPTY_RICH_TEXT_SERIALIZED;
	}, [normalizedInitialValue.content]);

	// ----- EFFECTS ----- //

	useEffect(() => {
		if (!isOpen) return;

		setDraftValue(normalizedInitialValue);
		setIsSaving(false);
	}, [isOpen, normalizedInitialValue]);

	/**
	 * handleSave
	 *
	 * Persists the current changes.
	 * @returns {Promise<void>} Resolves when the handler completes.
	 */
	async function handleSave() {
		if (isSaving) return;

		setIsSaving(true);

		try {
			await onSave?.(normalizeRichTextValue(draftValue));
		} finally {
			setIsSaving(false);
		}
	}
	// ----- RENDER ----- //

	return (
		<Modal
			height="85%"
			isOpen={isOpen}
			title={isNewBlock ? 'Add Text' : 'Edit Text'}
			width={isMobile ? '100%' : 880}
			onClose={onClose}
		>
			<div className="page-rich-text-block-modal-container">
				{/* COPY */}
				<div
					className="page-rich-text-block-modal-copy"
				>
					{`Write standalone ${normalizedDocumentLabel} copy for this block. The slot preview stays read-only so drag and layout controls remain stable.`}
				</div>

				{/* EDITOR */}
				<div className="page-rich-text-block-modal-editor">
					<RichTextEditor
						autoFocus={isOpen}
						placeholder={`Write ${normalizedDocumentLabel} copy...`}
						showInsertButton={false}
						toolbarPreset="article"
						value={draftValue}
						onChange={setDraftValue}
					/>
				</div>

				{/* ACTIONS */}
				<div className="page-rich-text-block-modal-actions">
					<Button
						borderColor={theme.backgroundHover}
						foregroundColor={theme.foreground}
						label="Cancel"
						onClick={onClose}
					/>
					<Button
						backgroundColor={theme.accent}
						borderColor={theme.accent}
						enabled={!isSaving}
						foregroundColor={theme.foreground}
						label={isSaving ? 'Saving...' : 'Save'}
						onClick={handleSave}
					/>
				</div>
			</div>
		</Modal>
	);
});

// Dev Tools
LayoutRichTextBlockModal.displayName = 'LayoutRichTextBlockModal';

export default LayoutRichTextBlockModal;
